import { CanActivateFn, Route, Routes, Router, UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { AppStore } from './store';
import { safePostLoginUrl, stashPostLoginReturnUrl } from './post-login-redirect';
import { DemoPersonaSwitchService } from './demo/demo-persona-switch.service';
import { personaSwitchAppliesToHost } from './demo/persona-switch.model';

const PUBLIC_PATH_PREFIXES = [
  'signed-out',
  'recovery',
  'login/hrd',
  'login/enroll-factor',
  'sign-up/invitation',
  // KYC front door (notes/kyc-front-door/): P0-A3 render unauthenticated. Prefix-with-slash-
  // boundary matching (see isPublicAppRoute below) means 'open' does NOT match '/openX' or any
  // other route that merely starts with the same four letters.
  'open',
] as const;

export const authShellGuard: CanActivateFn = (_route, state) => {
  const store = inject(AppStore);
  const router = inject(Router);
  const demoPersonaSwitcher = inject(DemoPersonaSwitchService);
  const hostApplies = typeof window !== 'undefined' && personaSwitchAppliesToHost(window.location.hostname);

  const decide = (): boolean | UrlTree => {
    if (!store.sessionChecked() || store.user() || isPublicAppRoute(state.url)) {
      return true;
    }
    // Demo/persona-switch hosts: a bootstrap FAILURE after a genuine root OIDC sign-in is not
    // "never signed in" -- rootSessionDetected() proves the BFF-only session probe found an
    // authenticated actual user (2026-09-20 public-demo-provisioning-428 defect: a real Keycloak
    // sign-in with no enrolled MFA factor makes POST /bff/demo/instance fail its factor-readiness
    // precondition; bootstrapState() settles to 'error' with store.user() still null because no
    // persona was ever bound). AppComponent's own root template already renders
    // tb-demo-bootstrap-gate's retry/sign-out face directly on the CURRENT route whenever
    // rootSessionDetected() is true, regardless of bootstrapState() -- this guard redirecting to
    // /signed-out here raced ahead of that gate and told a signed-in visitor "your session timed
    // out" instead of ever letting the gate's Retry/Sign out UI render. A session that was truly
    // never established (no root session detected at all) still falls through to the redirect
    // below exactly as before.
    if (hostApplies && demoPersonaSwitcher.rootSessionDetected()) {
      return true;
    }
    // The boot probe itself failed ('unavailable': 5xx/timeout/network), so "no user" is unknown,
    // not "signed out". AppComponent renders the gate's service-unavailable face with Retry in
    // place of the route; keep the deep link instead of claiming the session timed out.
    if (hostApplies && demoPersonaSwitcher.bootstrapState() === 'unavailable') {
      return true;
    }
    // Deep-link intent preservation: an unauthenticated deep link (e.g. /payments/pending/p-1)
    // must come BACK here after sign-in instead of dumping the user on /home. safePostLoginUrl
    // is the single open-redirect + non-resumable chokepoint ('' fallback = nothing worth
    // carrying). The target travels two ways: as a visible returnUrl query param on /signed-out
    // (SignedOutComponent re-stashes it before stripping params) and as the sessionStorage stash
    // consumed once by afterLogin() -- the BFF's OIDC round trip cannot carry a SPA-supplied
    // target (see post-login-redirect.ts, vector 4).
    const returnUrl = safePostLoginUrl(state.url, '');
    stashPostLoginReturnUrl(returnUrl);
    return router.createUrlTree(['/signed-out'], {
      queryParams: returnUrl ? { reason: 'user', returnUrl } : { reason: 'user' },
    });
  };

  // Demo-host bootstrap (app.component.ts's persona-switch effect) sets store.sessionChecked()
  // true as soon as a root BFF session is DETECTED -- before persona verification finishes --
  // so store.user() can be transiently null while sessionChecked() is already true. Judging that
  // exact instant as "signed out" was a real (if narrow) race: navigating away from /home fast
  // enough -- right after the demo shell's rootSessionDetected tick and before its 'ready' tick
  // -- bounced a genuinely-authenticating demo visitor to /signed-out?reason=user (found via the
  // consolidated-reporting-matrix e2e's demo-host cells failing intermittently: rotating cell
  // names, only ever the demo audience, the captured DOM showing the generic 'user' sign-out
  // screen mid-bootstrap). Wait out just that one transient window -- 'loading' with no user yet
  // -- before deciding; every other state (idle/ready/error) or host is judged exactly as before.
  if (hostApplies && demoPersonaSwitcher.bootstrapState() === 'loading' && !store.user()) {
    return toObservable(demoPersonaSwitcher.bootstrapState).pipe(
      filter((s) => s !== 'loading'),
      take(1),
      map(() => decide()),
    ) as Observable<boolean | UrlTree>;
  }

  return decide();
};

export function isPublicAppRoute(url: string): boolean {
  const path = url.split(/[?#]/, 1)[0].replace(/^\/+/, '');
  return PUBLIC_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(prefix + '/'),
  );
}

export function withAuthShellGuard(routes: Routes): Routes {
  return routes.map((route) => (isGuardExempt(route) ? route : {
    ...route,
    canActivate: [...(route.canActivate ?? []), authShellGuard],
  }));
}

function isGuardExempt(route: Route): boolean {
  if (!route.path || route.redirectTo !== undefined || route.path === '**') {
    return true;
  }
  return isPublicAppRoute('/' + route.path);
}
